import React, {ReactNode} from 'react'
import {StyledDiv} from '../../../../../../styledComponents/StyledTypeDefinitions'
import styled from 'styled-components'
import {Box} from '@mui/material'
import {ApplyTitle, FirstPageDiv, HeaderArea, HeaderAreaBox} from './NatAdminApplicatioPrintCommonStyle'
import {
    CONTRACTOR_REQUEST,
    RESIDENT_APPLY_PROCESS,
    RESIDENT_APPLY_STATUS
} from '../../../../../../CodeMaster'
import {getPrintDate} from '../../helper/NatApplicationHelper'
import {NatSealField, SealField} from './NatSealField'
import {convertToRoomNumberForResident, getFormattedZipCode} from '../../../../../common/util/Natutil'
import {ApplyStatusInfo} from '../../../repository/NatGetApplicationStatus'
import moment from 'moment'
import {NatEncloseInParenthesis} from './NatEncloseInParenthesis'
import {NatWindowFilmAttachApplyDetail} from '../../../repository/NatGetWindowFilmAttachApply';

interface Props {
    windowFilmAttachApplicationDocumentParam: NatWindowFilmAttachApplyDetail
    applyStatusInfoList: ApplyStatusInfo[]
}

export const NatWindowFilmAttachApplicationDocument: React.FC<Props> = props => {
    const {
        windowFilmAttachApplicationDocumentParam,
        applyStatusInfoList
    } = props

    const {
        applyId,
        condominiumName,
        ridgeName,
        roomNumber,
        applicantFamilyName,
        applicantFirstName,
        windowFilmAttachDate,
        contractorRequest,
        contractorName,
        contractorZipCode,
        contractorPrefecture,
        contractorAddress2,
        contractorAddress3,
        contractorBuildingRoomNumber,
        contractorPhoneNumber,
        contractorRep,
    } = windowFilmAttachApplicationDocumentParam

    const applicantRoom = `${ridgeName} ${convertToRoomNumberForResident(roomNumber)}`
    const applicantName = `${applicantFamilyName} ${applicantFirstName}`
    const contractorRequestLabel = `${CONTRACTOR_REQUEST.codeToEnumConstants(contractorRequest).label}`
    const contractorZipCodeLabel = contractorZipCode ? `${contractorZipCode}` : ``

    return (<>
        <FirstPageDiv>
            <HeaderArea>
                <HeaderAreaBox>
                    <Box ml={'auto'} mt={'10mm'}>申請ID：{applyId}</Box>
                </HeaderAreaBox>
                <HeaderAreaBox>
                    <Box m={'auto'}><ApplyTitle>窓ガラスフィルム貼付申請</ApplyTitle></Box>
                </HeaderAreaBox>
                <HeaderAreaBox>
                    <Box ml={'auto'} mt={'3mm'}>
                        {renderSealField(applyStatusInfoList)}
                    </Box>
                </HeaderAreaBox>
            </HeaderArea>
            <Box ml={'25mm'} mt={'3mm'}>
                理事長殿
            </Box>
            <Box ml={'30mm'} mt={'5mm'} whiteSpace={'pre-wrap'}>
                　私は、窓ガラスへのフィルム貼付を実施したいので、次の通り申込をいたします。<br/>
                なお当該フィルム貼付を原因として生じた問題（ガラスの熱割れを含む）を自主的に<br/>
                解決し、フィルムの貼付内容が規則に違反した場合は、私の責任においてフィルムの<br/>
                変更または原状回復をいたします。
            </Box>
            <Box margin={'3mm 0 0 30mm'} display={'flex'} flexDirection={'column'} width={'130mm'}>
                <Box>
                    <Box mb={'5mm'} whiteSpace={'pre-wrap'}>1  申請者</Box>
                    <Box ml={'8mm'} mb={'5mm'}>
                        {renderApplicationDetail('マンション名', condominiumName)}
                        {renderApplicationDetail('部屋番号', applicantRoom)}
                        {renderApplicationDetail('氏名', applicantName)}
                    </Box>
                </Box>
                <Box>
                    <Box mb={'5mm'}>
                        {renderApplicationDetail('2  貼付日', renderConstructionDate(windowFilmAttachDate))}
                    </Box>
                </Box>
                <Box>
                    <Box mb={'5mm'}>
                        {renderApplicationDetail('3  施工依頼', contractorRequestLabel)}
                    </Box>
                </Box>
                {(contractorRequest === CONTRACTOR_REQUEST.REQUEST.value) ?
                    <Box>
                        <Box mb={'5mm'} whiteSpace={'pre-wrap'}>
                            4  貼付施工者
                        </Box>
                        <Box ml={'8mm'} mb={'5mm'}>
                            {renderApplicationDetail('業者名', contractorName)}
                            {renderApplicationDetail('郵便番号', getFormattedZipCode(contractorZipCodeLabel, false))}
                            {renderApplicationDetail('都道府県', contractorPrefecture)}
                            {renderApplicationDetail('市区町村', contractorAddress2)}
                            {renderApplicationDetail('番地', contractorAddress3)}
                            {renderApplicationDetail('建物名・部屋番号', contractorBuildingRoomNumber)}
                            {renderApplicationDetail('電話番号', contractorPhoneNumber)}
                            {renderApplicationDetail('担当者', contractorRep)}
                        </Box>
                    </Box> : null
                }
            </Box>
        </FirstPageDiv>
    </>)

    NatWindowFilmAttachApplicationDocument.displayName = 'NatWindowFilmAttachApplicationDocument'
}

const renderApplicationDetail = (title: string, value: string | ReactNode) => {
    return (
        <PrintApplicationDetailContainer>
            <Box whiteSpace={'pre-wrap'}>{title}</Box>
            <NatEncloseInParenthesis item={value}/>
        </PrintApplicationDetailContainer>
    )
}

const renderConstructionDate = (date: string) => {
    const value = getPrintDate(date)

    return (
        <>
            <Box component={'span'} padding={'0 3mm 0 1mm'}>{value.year}</Box>年
            <Box component={'span'} padding={'0 4mm 0 4mm'}>{value.month}</Box>月
            <Box component={'span'} padding={'0 4mm 0 4mm'}>{value.day}</Box>日
        </>
    )
}

const renderSealField = (applyStatusInfoList: ApplyStatusInfo[]) => {
    const sealFields: SealField[] = applyStatusInfoList.map((info) => {
        const applyStatusEnum = RESIDENT_APPLY_PROCESS.codeToEnumConstants(info.applyProcess)
        let seal
        if (info.applyStatus === RESIDENT_APPLY_STATUS.REJECTION.value) {
            seal = '却下'
        } else {
            seal = info.actual.registerDatetime ? moment(info.actual.registerDatetime).format('YYYY/M/D') : ''
        }
        return {
            applyStatus: applyStatusEnum.label,
            seal: seal,
            sortOrder: applyStatusEnum.displayOrder
        }
    })

    return (
        <NatSealField sealFields={sealFields}/>
    )
}

const PrintApplicationDetailContainer: StyledDiv = styled.div`
  display: flex;
  width: 100%;
`
